import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createExecutionImage } from '@/lib/api/generated/api';
import type { ExecutionImageView } from '@/lib/api/generated/model';

import { toExecutionImageError } from '../errors';
import { executionImageKeys } from '../queryKeys';
import type { AddExecutionImageFormValues } from '../types';

/**
 * Add an image to the catalog (US3). `is_default` is never set here — it
 * defaults to `false` server-side; an image becomes the default only via the
 * dedicated set-default endpoint (`useSetDefaultExecutionImage`).
 *
 * Same raw-fetcher convention as `useCreateConnection`: calls
 * `createExecutionImage` directly so every non-201 status maps to a typed
 * refusal (`errors.ts`).
 */
export function useCreateExecutionImage(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      values: AddExecutionImageFormValues,
    ): Promise<ExecutionImageView> => {
      const response = await createExecutionImage(workspaceId, {
        connection_id: values.connectionId,
        image_reference: values.imageReference,
        label: values.label,
      });
      if (response.status === 201) return response.data;
      throw toExecutionImageError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executionImageKeys.all(workspaceId),
      });
    },
  });
}
